// Condition quality, best -> worst. Lower index = better.
export const CONDITION_RANK = [
  "Near Mint",
  "Lightly Played",
  "Moderately Played",
  "Heavily Played",
  "Damaged",
];

const conditionRank = (c) => {
  const i = CONDITION_RANK.indexOf(c);
  return i === -1 ? CONDITION_RANK.length : i;
};

// "Normal" sorts before every other finish.
const finishRank = (f) => (f === "Normal" ? -1 : 0);

export const parseSubType = (subtype) => {
  const s = String(subtype ?? "").trim();
  const idx = s.indexOf(" / ");
  // "Unknown" rather than a dash on both halves. These strings are RENDERED, in
  // the series picker and the spec list, and an em dash is banned product wide.
  // A dash in a condition column is also ambiguous: condition is real data on
  // this product, so "we were not told" has to say so.
  if (idx === -1) {
    return { finish: s || "Unknown", condition: "Unknown" };
  }
  return {
    finish: s.slice(0, idx).trim() || "Unknown",
    condition: s.slice(idx + 3).trim() || "Unknown",
  };
};

// Distinct series as { finish, condition, subtype }, preserving the original
// subtype string so we can map a (finish, condition) pair back to a dataKey.
const listSeries = (rows) => {
  const seen = new Map();
  (rows || []).forEach((r) => {
    const subtype = r.product_sub_type || "Ungraded";
    if (!seen.has(subtype)) {
      const { finish, condition } = parseSubType(subtype);
      seen.set(subtype, { finish, condition, subtype });
    }
  });
  return Array.from(seen.values());
};

export const transformData = (raw) => {
  if (!raw || !raw.length) return [];
  const map = new Map(); // dateKey -> { date, acc: { [subtype]: { sum, count } } }
  raw.forEach((item) => {
    const parsedDate = new Date(item.insertion_date);
    if (isNaN(parsedDate.getTime())) return; // skip rows with missing/invalid dates
    const dateKey = parsedDate.toISOString().slice(0, 10);
    const subtype = item.product_sub_type || "Ungraded";
    const price = +item.price || 0;
    if (!map.has(dateKey)) map.set(dateKey, { date: dateKey, acc: {} });
    const acc = map.get(dateKey).acc;
    if (!acc[subtype]) acc[subtype] = { sum: 0, count: 0 };
    acc[subtype].sum += price;
    acc[subtype].count += 1;
  });
  return Array.from(map.values())
    .map(({ date, acc }) => {
      const row = { date };
      Object.entries(acc).forEach(([subtype, { sum, count }]) => {
        row[subtype] = count ? sum / count : 0;
      });
      return row;
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date));
};

export const deriveFinishOptions = (rows) => {
  const finishes = [...new Set(listSeries(rows).map((s) => s.finish))];
  return finishes.sort((a, b) => {
    const ra = finishRank(a);
    const rb = finishRank(b);
    return ra !== rb ? ra - rb : a.localeCompare(b);
  });
};

export const deriveConditionOptions = (rows, finish) => {
  const conds = [
    ...new Set(
      listSeries(rows)
        .filter((s) => s.finish === finish)
        .map((s) => s.condition)
    ),
  ];
  return conds.sort((a, b) => {
    const ra = conditionRank(a);
    const rb = conditionRank(b);
    return ra !== rb ? ra - rb : a.localeCompare(b);
  });
};

export const pickDefaultSelection = (rows) => {
  const finishes = deriveFinishOptions(rows);
  if (!finishes.length) return { finish: null, condition: null };
  const finish = finishes[0];
  const conditions = deriveConditionOptions(rows, finish);
  return { finish, condition: conditions[0] ?? null };
};

export const resolveSubType = (rows, finish, condition) => {
  const match = listSeries(rows).find(
    (s) => s.finish === finish && s.condition === condition
  );
  return match ? match.subtype : null;
};

/**
 * Helpers over `price_matrix` -- the server's list of every positive
 * (finish, condition) a card is priced at.
 *
 * WHY THESE EXIST BESIDE THE `derive*` HELPERS ABOVE RATHER THAN REPLACING
 * THEM. Those read HISTORY rows and answer "what can this chart plot". These
 * read the PRICE MATRIX and answer "what is this card priced at". The two sets
 * are not the same: 8% of priced pairs have no history at all, so a card can
 * be priced at Near Mint with no Near Mint series to draw. The card page picks
 * its drop-down options from the matrix, because every option must have a
 * price; the chart then plots whichever of them it has data for.
 *
 * The reverse cannot happen -- a series always has a CardPrice row, since
 * PricePoint is a FK to it with ON DELETE CASCADE -- which is why there is no
 * union here, and no "priced at nothing" state to render.
 */
const rowsOf = (matrix) => (Array.isArray(matrix) ? matrix : []);

export const matrixFinishOptions = (matrix) => {
  const finishes = [...new Set(rowsOf(matrix).map((r) => r.finish))];
  return finishes.sort((a, b) => {
    const ra = finishRank(a);
    const rb = finishRank(b);
    return ra !== rb ? ra - rb : a.localeCompare(b);
  });
};

export const matrixConditionOptions = (matrix, finish) => {
  const conds = [
    ...new Set(
      rowsOf(matrix)
        .filter((r) => r.finish === finish)
        .map((r) => r.condition)
    ),
  ];
  return conds.sort((a, b) => {
    const ra = conditionRank(a);
    const rb = conditionRank(b);
    return ra !== rb ? ra - rb : a.localeCompare(b);
  });
};

/** EXACT, never a fallback. See `exact_price_for` on the server for why. */
export const matrixAmount = (matrix, finish, condition) => {
  const hit = rowsOf(matrix).find((r) => r.finish === finish && r.condition === condition);
  return hit ? hit.amount : null;
};

export const pairFromAnchor = (matrix, finish, condition) => {
  if (!finish || !condition) return null;
  const hit = rowsOf(matrix).find((r) => r.finish === finish && r.condition === condition);
  return hit ? { finish: hit.finish, condition: hit.condition } : null;
};
